// Common API types and response structures

/**
 * Standard API response wrapper for successful responses
 */
export interface ApiResponse<T = unknown> {
	data: T;
	success: true;
	message?: string;
}

/**
 * Standard API error response
 */
export interface ApiError {
	error: string;
	success: false;
	code?: string | number;
	details?: unknown;
}

/**
 * API response that can be either success or error
 */
export type ApiResult<T = unknown> = ApiResponse<T> | ApiError;

/**
 * Standard pagination parameters for API requests
 */
export interface PaginationParams {
	limit?: number;
	offset?: number;
	page?: number;
}

/**
 * Standard pagination metadata in responses
 */
export interface PaginationMeta {
	total: number;
	count: number;
	page: number;
	limit: number;
	hasMore: boolean;
}

/**
 * Paginated API response
 */
export interface PaginatedResponse<T> {
	data: T[];
	pagination: PaginationMeta;
	success: true;
}

/**
 * Search parameters for various endpoints
 */
export interface SearchParams {
	query?: string;
	filters?: Record<string, unknown>;
	sort?: string;
	order?: "asc" | "desc";
}

/**
 * Type guard to check if a response is an error
 */
export function isApiError(response: ApiResult): response is ApiError {
	return response.success === false;
}

/**
 * Type guard to check if a response is successful
 */
export function isApiSuccess<T>(
	response: ApiResult<T>,
): response is ApiResponse<T> {
	return response.success === true;
}

// Search types have been moved to features/search/index.ts
// Re-export them here for backward compatibility
export type {
	ArtistSearchResult,
	FestivalSearchResult,
	SearchResponse,
} from "../features/search";
